import { getWinningRosterIds, Match } from "model/Match/Match";
import { WorldTourSet } from "model/Match/Set/WorldTour/WorldTourSet";

export type GslSeasonRecord = {
  rosterId: string;
  matchesPlayed: number;
  matchesWon: number;
  tracksWon: number;
  roundsWon: number;
  position: number | undefined;
};

export type GslSeasonRecordWithDisplay = {
  record: GslSeasonRecord;
  orgId: string | undefined;
  displayName: string | undefined;
};

const createGslSeasonRecord = (rosterId: string): GslSeasonRecord => {
  return {
    rosterId,
    matchesPlayed: 0,
    matchesWon: 0,
    tracksWon: 0,
    roundsWon: 0,
    position: undefined,
  };
};

export const calculateGslSeasonRecords = (
  rosterIds: string[],
  matchesByPlayday: Match[][]
) => {
  const records = new Map<string, GslSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createGslSeasonRecord(id));
  });

  for (let playdayI = 0; playdayI < matchesByPlayday.length; playdayI++) {
    const matches = matchesByPlayday[playdayI];
    for (let matchI = 0; matchI < matches.length; matchI++) {
      const match = matches[matchI];
      if (!match.finalPositions) {
        continue;
      }

      const set = match.sets[0] as WorldTourSet;

      for (let rosterI = 0; rosterI < rosterIds.length; rosterI++) {
        const rosterId = rosterIds[rosterI];
        const rosterRecord = records.get(rosterId);
        const finalPos = match.finalPositions[rosterId];
        if (!rosterRecord || !finalPos) {
          continue;
        }

        rosterRecord.matchesPlayed++;
        if (getWinningRosterIds(match)?.includes(rosterId)) {
          rosterRecord.matchesWon++;
        }

        for (let trackI = 0; trackI < set.tracks.length; trackI++) {
          const track = set.tracks[trackI];
          if (!!track.finalPositions && track.finalPositions[rosterId] === 1) {
            rosterRecord.tracksWon++;
          }

          rosterRecord.roundsWon += track.scores[rosterId];
        }
      }
    }
  }

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  if (sortedRecords.length === 0) {
    return sortedRecords;
  }

  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }

  return sortedRecords;
};

const compareRecords = (a: GslSeasonRecord, b: GslSeasonRecord): number => {
  if (a.matchesWon !== b.matchesWon) {
    return b.matchesWon - a.matchesWon;
  }

  if (a.tracksWon !== b.tracksWon) {
    return b.tracksWon - a.tracksWon;
  }

  return b.roundsWon - a.roundsWon;
};
